import React from 'react';
import './CardTabs.css';

export type CardTab<K extends string> = {
  key: K;
  label: string;
  /** Record count shown after the label, e.g. "Users 76". */
  count?: number | null;
  /** The count already formatted (e.g. "3,348"); shown instead of `count`. */
  countLabel?: string | null;
  /** Status chip shown after the label, e.g. "Locked". */
  badge?: React.ReactNode;
  /** Draws a thin divider before this tab to start a second group (e.g. Inventory's Counts). */
  groupStart?: boolean;
};

export type CardTabsProps<K extends string> = {
  tabs: ReadonlyArray<CardTab<K>>;
  /** `null` while nothing is chosen yet (e.g. no email provider); the first tab is then the one Tab reaches. */
  activeKey: K | null;
  onChange: (key: K) => void;
  /** Names the tablist for assistive tech, e.g. "Service ticket type". */
  ariaLabel: string;
  idPrefix?: string;
  /** ID of the shared panel displayed by these tabs. */
  panelId?: string;
  /** Keep a single scrollable row in constrained surfaces such as dialogs. */
  overflow?: 'wrap' | 'scroll';
  /**
   * `segmented` draws a few short tabs as equal parts of one rounded track (phone layouts).
   * `record` is the record-detail strip (Installations, Events): a soft rounded bar whose
   * selected tab lifts onto the panel below with an accent top edge. It always scrolls.
   */
  appearance?: 'folder' | 'segmented' | 'record';
  /** The tabs split the strip's width equally: for a few short tabs, e.g. a library's two or three on a phone. */
  fill?: boolean;
  /** Extra class for page-specific placement only — not for restyling the tabs. */
  className?: string;
};

/**
 * Folder-style tabs that sit on top of a card and merge into its surface.
 *
 * The active tab drops its bottom border and shifts down 1px so it joins the
 * strip's bottom rule. That only reads correctly when the strip sits directly
 * above a `--surface` panel, so mount it flush against the top of the card it
 * switches (see the Service Tickets board and the Signature Invites library).
 */
export default function CardTabs<K extends string>({
  tabs,
  activeKey,
  onChange,
  ariaLabel,
  className,
  idPrefix,
  panelId,
  overflow = 'wrap',
  appearance = 'folder',
  fill = false,
}: CardTabsProps<K>) {
  const tablistRef = React.useRef<HTMLDivElement>(null);
  const scrolls = overflow === 'scroll' || appearance === 'record';
  const focusIndex = Math.max(0, tabs.findIndex(tab => tab.key === activeKey));
  React.useEffect(() => {
    const list = tablistRef.current;
    const active = list?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!list || !active || !scrolls) return;
    const bounds = list.getBoundingClientRect();
    const tabBounds = active.getBoundingClientRect();
    if (tabBounds.left < bounds.left) list.scrollLeft += tabBounds.left - bounds.left;
    else if (tabBounds.right > bounds.right) list.scrollLeft += tabBounds.right - bounds.right;
  }, [activeKey, scrolls]);
  return (
    <div
      ref={tablistRef}
      className={[
        'card-tabs',
        overflow === 'scroll' && appearance !== 'record' && 'card-tabs--scroll',
        appearance === 'segmented' && 'card-tabs--segmented',
        appearance === 'record' && 'card-tabs--record',
        fill && 'card-tabs--fill',
        className,
      ].filter(Boolean).join(' ')}
      role="tablist"
      aria-label={ariaLabel}
    >
      {tabs.map((tab, index) => (
        <React.Fragment key={tab.key}>
        {tab.groupStart && index > 0 ? <span className="card-tabs__divider" aria-hidden="true" /> : null}
        <button
          type="button"
          role="tab"
          id={idPrefix ? `${idPrefix}-${tab.key}-tab` : undefined}
          aria-controls={panelId}
          aria-selected={activeKey === tab.key}
          tabIndex={index === focusIndex ? 0 : -1}
          className="card-tabs__tab"
          data-active={activeKey === tab.key || undefined}
          onClick={() => onChange(tab.key)}
          onKeyDown={event => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
              : (index + (event.key === 'ArrowLeft' ? -1 : 1) + tabs.length) % tabs.length;
            onChange(tabs[nextIndex].key);
            tablistRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[nextIndex]?.focus({ preventScroll: true });
          }}
        >
          {tab.label}
          {tab.countLabel != null || tab.count != null ? <span className="card-tabs__count">{tab.countLabel ?? tab.count}</span> : null}
          {tab.badge != null ? <span className="card-tabs__badge">{tab.badge}</span> : null}
        </button>
        </React.Fragment>
      ))}
    </div>
  );
}
